/**
 * PreviewCardCH — "How it looks in the library" preview for a
 * Challenge. Mirrors TheTappingSolutionWeb's `ActiveChallengeCard`
 * (used by the "My Challenges" section on /challenges) which is
 * the most representative variant — it's how challenges show up
 * once the user has joined them:
 *
 *   • Full-bleed challenge image card on top, with:
 *       – Free badge (top-left) when `challenge_is_free`
 *       – New ribbon (top-right) when `challenge_is_new`
 *       – "Joined" count at bottom-left, matching the WebApp's
 *         `ActiveChallengeCard` exactly. `total_joins` rides
 *         along on the CMS `GET /challenges/{id}` payload
 *         (ChallengeResponseResource ships it) and gets spread
 *         into form state via `...currentChallenge`. New
 *         (unsaved) challenges fall back to a dashed placeholder.
 *   • Progress bar below the card (yellow→orange gradient on a
 *     dark track, synthetic 35% to demo the bar in the absence
 *     of a real user-progress signal)
 *   • "0 / total" completed counter to the right of the bar
 *
 * Footer: universal_link path + Open ↗ link (matches all the
 * other preview cards).
 */
import React from 'react';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';

export default function PreviewCardCH({ state }) {
  const isFree = !!state.challenge_is_free;
  const isNew = !!state.challenge_is_new;

  // The WebApp's ChallengeCard renders `challenge_image`, not the
  // wide art. The wide art is a different crop (often with the
  // title baked in), so showing it here cut the title off on both
  // sides. Falls back to the wide art, then a navy placeholder.
  const imageUrl =
    state.challenge_image_preview
    || state.challenge_image
    || state.challenge_image_wide_preview
    || state.challenge_image_wide
    || '';

  const totalDays = Number(state.total_days) || 0;

  // `total_joins` comes from ChallengeResponseResource on the
  // edit-mode fetch. New / unsaved challenges have no joins yet,
  // so render an em-dash placeholder instead of the formatted
  // "0 joined" which would read as a real metric.
  const totalJoins = Number(state.total_joins);
  const hasJoinData = Number.isFinite(totalJoins);
  const joinsLabel = hasJoinData
    ? `${totalJoins.toLocaleString()} joined`
    : '— joined';

  // Synthetic progress: shows the bar at a comfortable mid-point
  // so authors can see the recipe (yellow→orange on dark track).
  // The denominator is the total day count when set; 0 / 0 reads
  // as "—" so an unconfigured challenge doesn't claim progress.
  const PREVIEW_PROGRESS_PCT = 35;
  const completed = totalDays > 0 ? Math.round((PREVIEW_PROGRESS_PCT / 100) * totalDays) : 0;
  const counterLabel = totalDays > 0
    ? `${completed}/${totalDays} completed`
    : '— / — completed';

  const universalLink = state.universal_link || null;
  const previewPath = universalLink
    ? universalLink.replace(/^https?:\/\//, '')
    : '— not yet generated —';

  return (
    <RailCard
      variant="preview"
      head={({ open, toggle }) => (
        <RailCardHead
          variant="preview"
          eyebrow="How it looks in the library"
          open={open}
          onToggle={toggle}
        />
      )}
    >
      {/* Stage — beige matte behind the active-challenge card. */}
      <div
        style={{
          padding: 16,
          background: 'linear-gradient(180deg, #FAF7F2 0%, #F5EDE4 100%)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <div className="flex flex-col" style={{ width: '100%', gap: 6 }}>
          {/* Image card (ChallengeCard) */}
          <div
            data-testid="challenge-preview-image"
            style={{
              position: 'relative',
              width: '100%',
              // WebApp CHALLENGE_CARD (aspectRatioGridCards.ts): 300 × 177.
              aspectRatio: '300 / 177',
              borderRadius: 12,
              overflow: 'hidden',
              backgroundImage: imageUrl ? `url("${imageUrl}")` : 'none',
              backgroundColor: imageUrl ? 'transparent' : '#2C3E50',
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              boxShadow: '0 6px 16px #2C3E5025',
            }}
          >
            {/* Subtle bottom-up gradient — keeps the joined label
                legible without dominating the image. */}
            <span
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0) 100%)',
                pointerEvents: 'none',
              }}
            />

            {/* Free badge — top-left */}
            {isFree && (
              <span
                className="font-condensed font-bold uppercase"
                style={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  fontSize: 11,
                  letterSpacing: '0.08em',
                  color: '#C4715B',
                  background: '#FFFCF8',
                  padding: '4px 11px',
                  borderRadius: 999,
                  boxShadow: '0 2px 6px #00000028',
                }}
              >
                Free
              </span>
            )}

            {/* New ribbon — top-right */}
            {isNew && (
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 70,
                  height: 70,
                  overflow: 'hidden',
                  pointerEvents: 'none',
                }}
              >
                <span
                  className="font-condensed font-bold uppercase"
                  style={{
                    position: 'absolute',
                    top: 11,
                    right: -26,
                    transform: 'rotate(45deg)',
                    background: 'linear-gradient(135deg, #FFC107, #F5A623)',
                    color: '#2C3E50',
                    fontSize: 10,
                    letterSpacing: '0.12em',
                    padding: '4px 28px',
                    boxShadow: '0 2px 6px #00000028',
                  }}
                >
                  New
                </span>
              </span>
            )}

            {/* Bottom-left — "N joined", matching the WebApp's
                ChallengeCard footer. */}
            <div
              style={{
                position: 'absolute',
                bottom: 10,
                left: 12,
                fontFamily: 'Asap, sans-serif',
                fontSize: 12,
                letterSpacing: '0.02em',
                color: '#FFFFFF',
                textShadow: '0 1px 3px #00000060',
                opacity: hasJoinData ? 1 : 0.65,
              }}
            >
              {joinsLabel}
            </div>
          </div>

          {/* Progress bar row, sized to mirror the WebApp's
              ActiveChallengeCard footer (h-2.5 track + side label). */}
          <div
            className="flex items-center"
            style={{ gap: 10, padding: '0 4px', marginTop: 4 }}
          >
            <div
              role="progressbar"
              aria-valuenow={PREVIEW_PROGRESS_PCT}
              aria-valuemin={0}
              aria-valuemax={100}
              style={{
                flex: 1,
                height: 10,
                borderRadius: 999,
                background: '#EFE7D8', /* cream-dark progress track */
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${totalDays > 0 ? PREVIEW_PROGRESS_PCT : 0}%`,
                  height: '100%',
                  background: 'linear-gradient(180deg, #FFC435 0%, #FF9927 100%)',
                  borderRadius: 999,
                  transition: 'width 200ms ease',
                }}
              />
            </div>
            <span
              className="text-text-muted"
              style={{
                fontSize: 11,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                fontFamily: 'Asap, sans-serif',
              }}
            >
              {counterLabel}
            </span>
          </div>
        </div>
      </div>

      {/* preview-foot — URL + Open link */}
      <div
        className="flex items-center justify-between"
        style={{
          padding: '10px 14px',
          borderTop: '1px solid #E8E0CF',
          background: '#FAF4EA',
          fontSize: 11,
          gap: 8,
        }}
      >
        <span
          className="font-mono text-text-muted"
          style={{
            fontSize: 10.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            minWidth: 0,
          }}
        >
          {previewPath}
        </span>
        {universalLink && (
          <a
            href={universalLink}
            target="_blank"
            rel="noopener noreferrer"
            className="font-condensed font-bold uppercase no-underline"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.08em',
              color: '#5C7E72',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >
            Open ↗
          </a>
        )}
      </div>
    </RailCard>
  );
}
